import { path, pathOr } from 'ramda';
import { AnyAction } from 'redux';
import { Event, ParsedRedirect, RedirectCampaignParam } from '../types';
import {
  CHANGE_CASHIER_RESULT_URL,
  CHANGE_INTERFACE_HEADER,
  CHANGE_LANGUAGE_ERROR,
  CHANGE_PAGE,
  GET_PARAM_AMS_SEGMENTS,
  GET_PARAM_ASSETS_MANIFEST,
  GET_PARAM_BASE_URL,
  GET_PARAM_BRAND,
  GET_PARAM_CHANNEL,
  GET_PARAM_CLIENT_ID,
  GET_PARAM_CONTAINER,
  GET_PARAM_CROSS_DOMAIN_CLICK_STREAM_ID,
  GET_PARAM_DEPOSIT_URL,
  GET_PARAM_LANGUAGE,
  GET_PARAM_LAYOUT_DIMENSIONS,
  GET_PARAM_PLATFORM,
  GET_PARAM_PROXY_PREFIX,
  GET_PARAM_THEME,
  GET_PARAM_USER_DATA,
  GET_PARAM_USER_TOKEN,
  GET_PARAM_WITHDRAWAL_URL,
  GET_PARAM_BRAND_HASH,
  GET_PARAM_APPLICATION_ID,
  GET_PARAM_OPERATOR_ID,
  GET_PARAM_API_KEY,
  REDIRECT_USER_BONUSES,
  REDIRECT_VIA_DEEP_LINK,
  REDIRECT_VIA_PLAIN_DEEP_LINK,
  REQUEST_APPLICATION_INSTALL_MODAL,
  REQUEST_DEPOSIT_MODAL,
  REQUEST_LOGIN_MODAL,
  REQUEST_UPDATE_BALANCE,
  SERVICE_RENDER_ERROR,
  UPDATE_NAVBAR,
} from '../constants';
import {
  ChangeInterfaceIds,
  DynamicIds,
  EventIds,
  EventTypes,
  RequestDataIds,
  RequestModalIds,
  RequestUpdateIds,
  UpdateEventIds,
} from '../constants/ABA';

export const shellEventFromAction = (action: AnyAction): Event => {
  switch (action.type) {
    // *
    // Params

    case GET_PARAM_CONTAINER:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.RenderContainer,
      };

    case GET_PARAM_LANGUAGE:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.Language,
      };

    case GET_PARAM_BASE_URL:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.BaseUrl,
      };

    case GET_PARAM_BRAND:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.Brand,
      };

    case GET_PARAM_CHANNEL:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.Channel,
      };

    case GET_PARAM_PROXY_PREFIX:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.ProxyPrefix,
      };

    case GET_PARAM_USER_TOKEN:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.UserToken,
      };

    case GET_PARAM_USER_DATA:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.UserData,
      };

    case GET_PARAM_CLIENT_ID:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.ClientId,
      };

    case GET_PARAM_PLATFORM:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.Platform,
      };

    case GET_PARAM_DEPOSIT_URL:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.DepositUrl,
      };

    case GET_PARAM_WITHDRAWAL_URL:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.WithdrawalUrl,
      };

    case GET_PARAM_BRAND_HASH:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.BrandHash,
      };

    case GET_PARAM_APPLICATION_ID:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.ApplicationId,
      };

    case GET_PARAM_OPERATOR_ID:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.Operator,
      };

    case GET_PARAM_API_KEY:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.APIKey,
      };

    case GET_PARAM_LAYOUT_DIMENSIONS:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.LayoutDimensions,
      };

    case GET_PARAM_ASSETS_MANIFEST:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.AssetsManifest,
      };

    case GET_PARAM_AMS_SEGMENTS:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.AmsSegments,
      };

    case GET_PARAM_THEME:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.Theme,
      };

    case GET_PARAM_CROSS_DOMAIN_CLICK_STREAM_ID:
      return {
        type: EventTypes.RequestData,
        id: RequestDataIds.CrossDomainClickStreamId,
      };

    // Request modals

    case REQUEST_LOGIN_MODAL:
      return {
        type: EventTypes.RequestModal,
        id: RequestModalIds.Login,
        payload: {},
      };

    case REQUEST_DEPOSIT_MODAL:
      return {
        type: EventTypes.RequestModal,
        id: RequestModalIds.Deposit,
        payload: {},
      };

    case REQUEST_APPLICATION_INSTALL_MODAL:
      return {
        type: EventTypes.RequestModal,
        id: RequestModalIds.ApplicationInstall,
        payload: {},
      };

    // Request updates

    case REQUEST_UPDATE_BALANCE:
      return {
        type: EventTypes.RequestUpdate,
        id: RequestUpdateIds.Balance,
        payload: {},
      };

    // Redirect via deep links

    case REDIRECT_VIA_PLAIN_DEEP_LINK:
      return {
        type: EventTypes.Event,
        id: EventIds.Redirect,
        payload: {
          data: action.payload,
        },
      };

    case REDIRECT_VIA_DEEP_LINK:
      return {
        type: EventTypes.Event,
        id: EventIds.Redirect,
        payload: {
          data: getRedirectDeepLink(action.payload),
        },
      };

    case CHANGE_CASHIER_RESULT_URL:
      return {
        type: EventTypes.Event,
        id: EventIds.ChangeCashierResultURL,
        payload: {
          data: getRedirectDeepLink(action.payload),
        },
      };

    // other

    case SERVICE_RENDER_ERROR: {
      let error = action.payload;

      if (!action.error || !action.payload) {
        error = new Error('Unexpected service render error.');
      }

      return {
        type: EventTypes.Event,
        id: EventIds.ServiceRender,
        payload: { error },
      };
    }

    case CHANGE_LANGUAGE_ERROR: {
      let error = action.payload;

      if (!action.error || !action.payload) {
        error = new Error('Unexpected change language error.');
      }

      return {
        type: EventTypes.Event,
        id: EventIds.ChangeLanguage,
        payload: { error },
      };
    }

    case CHANGE_PAGE:
      return {
        type: EventTypes.Event,
        id: pathOr('', ['payload', 'id'], action),
        payload: path(['payload', 'event'], action),
      };

    case CHANGE_INTERFACE_HEADER:
      return {
        type: EventTypes.Event,
        id: ChangeInterfaceIds.Header,
        payload: {
          isShown: action.payload,
        },
      };

    case REDIRECT_USER_BONUSES:
      return {
        type: EventTypes.Event,
        id: EventIds.RedirectUserBonuses,
        payload: {},
      };

    case UPDATE_NAVBAR:
      return {
        type: EventTypes.Event,
        id: UpdateEventIds.Navbar,
        payload: action.payload,
      };
    default:
      return { type: EventTypes.Event, id: DynamicIds.Unhandled };
  }
};

export function getRedirectDeepLink(config: ParsedRedirect): string | null {
  if (!config) return null;

  const { protocol, channel, campaign_type, campaign_permanent, campaign_param } = config;

  const url = new URL(`${protocol}${channel}`);
  url.searchParams.append('campaign_type', campaign_type);
  url.searchParams.append('campaign_permanent', campaign_permanent.toString());

  if (campaign_param) {
    const params = getRedirectCampaignParam(campaign_param);
    url.searchParams.append('campaign_param_mvp', params);
    url.searchParams.append('campaign_param', params);
  }

  return url.toString();
}

export function getRedirectCampaignParam(campaignParam: RedirectCampaignParam): string {
  if (typeof campaignParam === 'object') {
    return JSON.stringify(campaignParam);
  }
  return campaignParam.toString();
}
